/**
 * A server timestamp, shown in Israel time — one rule for the record page.
 *
 * 2026-10-10 (owner: "אמור להיות שעון ישראל"). The same column holds three shapes,
 * depending on which code wrote it:
 *
 *   2026-10-10 19:54:52.993601+02   PostgreSQL CURRENT_TIMESTAMP cast to a TEXT column,
 *                                   in the database's zone (Europe/Berlin) — explicit offset
 *   2026-10-10T20:46:03.024921      Python datetime.now().isoformat() — no zone; Israel time
 *                                   (every writer process is pinned, shared/israel_time.py)
 *   2025-03-01T10:00:00.000+0000    Salesforce export — explicit offset
 *
 * The record grid printed the raw string, and `new Date(...)` is not a safe parser for the
 * first shape (a two-digit offset is not ISO; some browsers return Invalid Date).
 *
 * Rule: an explicit offset is honoured and converted to Asia/Jerusalem; a value without
 * one is already Israel wall time and is shown as written — it is never shifted by the
 * viewer's own clock.
 */

const DT = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?\s*(Z|[+-]\d{2}(?::?\d{2})?)?$/;

const ISRAEL = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Jerusalem',
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});

const pad = (n) => String(n).padStart(2, '0');

function offsetMinutes(tz) {
  if (tz === 'Z') return 0;
  const sign = tz[0] === '-' ? -1 : 1;
  const digits = tz.slice(1).replace(':', '');
  const hours = Number(digits.slice(0, 2));
  const minutes = digits.length > 2 ? Number(digits.slice(2, 4)) : 0;
  return sign * (hours * 60 + minutes);
}

/**
 * 'dd/mm/yyyy HH:MM' in Israel time, or null when `value` is not a date-time string
 * (a date alone, a number, free text) — the caller then shows it as it did before.
 */
export function formatIsraelDateTime(value) {
  if (typeof value !== 'string') return null;
  const m = value.trim().match(DT);
  if (!m) return null;
  const [, y, mo, d, h, mi, s, tz] = m;
  if (!tz) {
    return `${d}/${mo}/${y} ${h}:${mi}`;
  }
  const utcMs = Date.UTC(+y, +mo - 1, +d, +h, +mi, +(s || 0)) - offsetMinutes(tz) * 60000;
  const parts = Object.fromEntries(ISRAEL.formatToParts(new Date(utcMs)).map((p) => [p.type, p.value]));
  return `${parts.day}/${parts.month}/${parts.year} ${pad(Number(parts.hour) % 24)}:${parts.minute}`;
}
